import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, TrendingUp, ArrowRight, Sparkles, Clock, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface RuleProposal {
  ruleId: string;
  current: {
    accuracy: number;
    sampleSize: number;
    description?: string;
  };
  proposed: {
    accuracy: number;
    confidence: number;
    description?: string;
  };
  uplift: number;
  rollbackTo: number;
}

interface ImprovementCardProps {
  proposal: RuleProposal;
  onApprove?: (ruleId: string) => void;
  onReject?: (ruleId: string) => void;
}

const ImprovementCard = ({ proposal, onApprove, onReject }: ImprovementCardProps) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [status, setStatus] = useState<"pending" | "approved" | "rejected">("pending");

  const handleApprove = async () => {
    setIsApproving(true);
    try {
      // Call optimize-rules to apply the improvement
      const { data, error } = await supabase.functions.invoke("optimize-rules", {
        body: {
          action: "apply",
          ruleId: proposal.ruleId,
        },
      });

      if (error) throw error;

      setStatus("approved");
      toast.success("Rule improvement applied successfully!");
      onApprove?.(proposal.ruleId);
    } catch (error) {
      console.error("Error applying rule:", error);
      toast.error("Failed to apply improvement");
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    setIsRejecting(true);
    try {
      setStatus("rejected");
      toast.info("Improvement rejected. Current rule kept.");
      onReject?.(proposal.ruleId);
    } catch (error) {
      console.error("Error rejecting rule:", error);
    } finally {
      setIsRejecting(false);
    }
  };

  const formatRuleId = (ruleId: string) => {
    return ruleId
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (status === "approved") {
    return (
      <Card className="border-success/50 bg-success/5">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-success">
            <Check className="h-5 w-5" />
            <span className="font-medium">Improvement applied to {formatRuleId(proposal.ruleId)}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === "rejected") {
    return (
      <Card className="border-muted bg-muted/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-muted-foreground">
            <X className="h-5 w-5" />
            <span className="font-medium">Improvement rejected for {formatRuleId(proposal.ruleId)}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-brand-200 hover:border-brand-300 transition-colors">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand-500" />
            <CardTitle className="text-lg">{formatRuleId(proposal.ruleId)}</CardTitle>
          </div>
          <Badge variant="secondary" className="bg-brand-100 text-brand-700">
            <TrendingUp className="h-3 w-3 mr-1" />
            +{(proposal.uplift * 100).toFixed(1)}% uplift
          </Badge>
        </div>
        <CardDescription>AI found a better configuration for this rule</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Before/After Comparison */}
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 bg-muted/50 rounded-lg border">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Current</p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Accuracy</span>
                <span className="font-semibold">{(proposal.current.accuracy * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Sample Size</span>
                <span className="font-medium">{proposal.current.sampleSize}</span>
              </div>
              {proposal.current.description && (
                <p className="text-xs text-muted-foreground mt-2">{proposal.current.description}</p>
              )}
            </div>
          </div>

          <div className="p-4 bg-brand-50 rounded-lg border border-brand-200">
            <p className="text-xs font-medium text-brand-600 uppercase tracking-wider mb-2">Proposed</p>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Accuracy</span>
                <span className="font-semibold text-brand-600">{(proposal.proposed.accuracy * 100).toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Confidence</span>
                <span className="font-medium">{(proposal.proposed.confidence * 100).toFixed(0)}%</span>
              </div>
              {proposal.proposed.description && (
                <p className="text-xs text-brand-700 mt-2">{proposal.proposed.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Visual Arrow */}
        <div className="flex justify-center">
          <div className="flex items-center gap-2 text-brand-500">
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>

        {/* Rollback Notice */}
        <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg text-sm text-muted-foreground">
          <Clock className="h-4 w-4 mt-0.5 shrink-0" />
          <span>You can roll back to version {proposal.rollbackTo} anytime if results don't improve.</span>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleReject}
            disabled={isApproving || isRejecting}
          >
            {isRejecting ? (
              <>Rejecting...</>
            ) : (
              <>
                <X className="mr-2 h-4 w-4" />
                Keep Current
              </>
            )}
          </Button>
          <Button
            className="flex-1 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600"
            onClick={handleApprove}
            disabled={isApproving || isRejecting}
          >
            {isApproving ? (
              <>Applying...</>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />
                Apply Improvement
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ImprovementCard;
